import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { DataRoomTeaserPreview } from '../../shared/models';

interface TeaserPageActionButtonProps {
	tab: string;
	selectedTeasers: string[];
	teasersReady: boolean;
	teaserPreview: DataRoomTeaserPreview | null;
	hasLaunched: boolean;
	onTeaserPreview: (preview: DataRoomTeaserPreview | null) => void;
	onEditSummary: (bool: boolean) => void;
	onBlukIgnoreAtLaunch: (bool: boolean) => void;
	onBlukIncludeAtLaunch: (bool: boolean) => void;
	onBulkSend: (bool: boolean) => void;
	onBulkDelete: (bool: boolean) => void;
}

type TeaserPageActionItem = keyof typeof TeaserPageActionItemMap;
const TeaserPageActionItemMap = {
	EmailPreview: 'Email Preview',
	EditSummaryText: 'Edit Summary Text',
	SendAll: 'Send All',
	IgnoreAtLaunch: 'Ignore at Launch',
	IncludeAtLaunch: 'Include at Launch',
	SendSelected: 'Send Selected',
	DeleteSelected: 'Delete Selected',
};

export const TeaserPageActionButton: React.FC<TeaserPageActionButtonProps> = ({
	tab,
	selectedTeasers,
	teasersReady,
	teaserPreview,
	hasLaunched,
	onTeaserPreview,
	onEditSummary,
	onBlukIgnoreAtLaunch,
	onBlukIncludeAtLaunch,
	onBulkSend,
	onBulkDelete,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const isPendingTab = tab == 'unsent' || tab == 'sent';
	const showIgnoreIncludeOptions = isPendingTab && !hasLaunched;

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: TeaserPageActionItem) => {
		switch (action) {
			case 'EmailPreview':
				onTeaserPreview(teaserPreview);
				break;
			case 'EditSummaryText':
				onEditSummary(true);
				break;
			case 'SendAll':
			case 'SendSelected':
				onBulkSend(true);
				break;
			case 'DeleteSelected':
				onBulkDelete(true);
				break;
			case 'IgnoreAtLaunch':
				onBlukIgnoreAtLaunch(true);
				break;
			case 'IncludeAtLaunch':
				onBlukIncludeAtLaunch(true);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: TeaserPageActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Teaser Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'EmailPreview')}>
										{TeaserPageActionItemMap['EmailPreview']}
									</MenuItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'EditSummaryText')}>
										{TeaserPageActionItemMap['EditSummaryText']}
									</MenuItem>
									{isPendingTab && (
										<MenuItem
											disabled={!teasersReady}
											onClick={event => handleMenuItemClick(event, 'SendAll')}
										>
											{TeaserPageActionItemMap['SendAll']}
										</MenuItem>
									)}
									{isPendingTab && (
										<MenuItem
											disabled={
												selectedTeasers == null ||
												selectedTeasers == undefined ||
												selectedTeasers.length == 0 ||
												!teasersReady
											}
											onClick={event => handleMenuItemClick(event, 'SendSelected')}
										>
											{TeaserPageActionItemMap['SendSelected']}
										</MenuItem>
									)}
									{showIgnoreIncludeOptions && (
										<MenuItem
											disabled={
												selectedTeasers == null ||
												selectedTeasers == undefined ||
												selectedTeasers.length == 0
											}
											onClick={event => handleMenuItemClick(event, 'IgnoreAtLaunch')}
										>
											{TeaserPageActionItemMap['IgnoreAtLaunch']}
										</MenuItem>
									)}
									{showIgnoreIncludeOptions && (
										<MenuItem
											disabled={
												selectedTeasers == null ||
												selectedTeasers == undefined ||
												selectedTeasers.length == 0
											}
											onClick={event => handleMenuItemClick(event, 'IncludeAtLaunch')}
										>
											{TeaserPageActionItemMap['IncludeAtLaunch']}
										</MenuItem>
									)}
									{isPendingTab && (
										<MenuItem
											disabled={
												selectedTeasers == null ||
												selectedTeasers == undefined ||
												selectedTeasers.length == 0
											}
											onClick={event => handleMenuItemClick(event, 'DeleteSelected')}
										>
											{TeaserPageActionItemMap['DeleteSelected']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
